import axios from 'axios';
import qs from 'query-string';
import API_URLS from '../../constants/api-urls';
import { INTERACTIVE_HOST } from '../../constants/interactive-urls';
import { world } from '../../languages';
import getDisposableToken from '../../services/authorization/get-disposable-token';
import { storageKeys } from '../browser-storage/storage-keys';
import { isValidUrl } from '../url/is-valid-url';
import serializeQueryParams from '../url/serialize-query-params';

type WhitelistedDomainDetails = {
    allowedProtocols: string[];
    name: string;
    allowSubdomains?: boolean;
};

export const REDIRECT_STORAGE_KEY = 'interactiveRedirectLink';

const isWhiteListedDomain = (link: string) => {
    const domainsWhiteList: WhitelistedDomainDetails[] = [
        {
            allowedProtocols: ['http', 'https'],
            name: 'localhost'
        },
        {
            allowedProtocols: ['https'],
            name: 'i.eduki.info',
            allowSubdomains: true
        },
        {
            allowedProtocols: ['https'],
            name: 'i.eduki.dev',
            allowSubdomains: true
        },
        {
            allowedProtocols: ['https'],
            name: 'i.eduki.com',
            allowSubdomains: true
        }
    ];

    try {
        const parsedURL = new URL(link);
        const { hostname } = parsedURL;

        return domainsWhiteList.some((domainDetails) => {
            const isHostMatch =
                hostname === domainDetails.name ||
                (Boolean(domainDetails.allowSubdomains) && hostname.endsWith(`.${domainDetails.name}`));

            return isHostMatch && isAllowedProtocol(domainDetails, parsedURL.protocol.replace(':', ''));
        });
    } catch (e) {
        return false;
    }
};

const isAllowedProtocol = (domainDetails: WhitelistedDomainDetails, parsedURLProtocol: string) => {
    return domainDetails.allowedProtocols.some((protocol) => protocol === parsedURLProtocol);
};

export const isValidInteractiveURL = (link: string) => {
    return isValidUrl(link) && isWhiteListedDomain(link);
};

const interactiveRedirectHelper = {
    checkURLAndStoreRedirectLink: () => {
        const queryParams = serializeQueryParams();
        const link = decodeURIComponent(queryParams.next as string);

        if (isValidInteractiveURL(link)) {
            window.storage.setItem(REDIRECT_STORAGE_KEY, link);
        }
    },
    clearStorage: () => {
        window.storage.removeItem(REDIRECT_STORAGE_KEY);
    },
    getMaterialLink(id: number | string) {
        return `${INTERACTIVE_HOST}/material/${id}?world=${world}`;
    },
    getShareMaterialLink(id: number | string) {
        return `${INTERACTIVE_HOST}/material/${id}/share?world=${world}`;
    },
    goToClassroom(classroomId: string) {
        return `${INTERACTIVE_HOST}/teacher/room/${classroomId}?world=${world}`;
    },
    getMaterialPreviewLink(id: number | string, hideHeader?: boolean, tryNow?: boolean) {
        const paramsObj: { [key: string]: string | boolean } = {
            world
        };

        if (hideHeader) {
            paramsObj.hideHeader = true;
        }

        if (tryNow) {
            paramsObj.trynowButton = 'showButton';
        }

        return `${INTERACTIVE_HOST}/materialPreview/${id}?${qs.stringify(paramsObj)}`;
    },
    getRedirectLinkFromURL: () => {
        const queryParams = serializeQueryParams();

        try {
            const link = decodeURIComponent(queryParams.next as string);

            if (!isValidInteractiveURL(link)) {
                return '';
            }

            return link;
        } catch (e) {
            return '';
        }
    },
    getStoredRedirectLink: () => {
        const storedRedirectLink = window.storage.getItem(REDIRECT_STORAGE_KEY);
        const link = decodeURIComponent(storedRedirectLink as string);

        if (!isValidInteractiveURL(link)) {
            return '';
        }

        return link;
    },
    goTo(
        url: string,
        accessToken = window.storage.getItem(storageKeys.auth.accessToken),
        userId = window.storage.getItem(storageKeys.user.userId)
    ) {
        getDisposableToken('redirect', accessToken, userId)
            .then((response) => {
                const disposableToken = response.access_token;
                const urlWithToken = qs.stringifyUrl(
                    {
                        query: {
                            disposable_token: disposableToken,
                            world
                        },
                        url
                    },
                    {
                        encode: false
                    }
                );

                window.locationReload(urlWithToken);
            })
            .catch(() => {
                window.locationReload(url);
            });
    },
    goToStoredRedirectLinkIfSome(fallback = () => {}) {
        const storedInteractiveRedirectLink = this.getStoredRedirectLink();

        if (storedInteractiveRedirectLink) {
            interactiveRedirectHelper.clearStorage();
            interactiveRedirectHelper.goTo(storedInteractiveRedirectLink);
        } else {
            fallback();
        }
    },
    pollEdukiMaterialCreationStatus(id: number) {
        const POLLING_DELAY = 1000;
        const TIMEOUT = 60000;
        const query = `{
            materials_legacy (
                    filter: {id: ${id}}
                ) { 
                    id
                    isActive
                    interactiveId
                    title
                    description
                    isUnitPublishable
                    isShadow
                    world
                    isAllowedToShare
                    author {
                        id
                        isSeller
                        details {
                            publicName
                            imagePath
                        }
                    }
                    firstPreviewImage { plain }
                }
        }`;

        return new Promise((resolve, reject) => {
            let timedOut = false;

            const timeoutTimer = setTimeout(() => {
                timedOut = true;
                reject(new Error('Polling timed out after 1 minute'));
            }, TIMEOUT);

            const fetchMaterial = () => {
                if (timedOut) {
                    return;
                }

                axios
                    .post(API_URLS.INTERACTIVE_INDEXER, {
                        query
                    })
                    .then((response) => {
                        if (timedOut) {
                            return;
                        }

                        const material = response.data.materials_legacy[0];

                        if (material.id === id) {
                            clearTimeout(timeoutTimer);
                            resolve({ ready: true });
                        } else {
                            delayedFetch();
                        }
                    })
                    .catch(() => {
                        if (timedOut) {
                            return;
                        }

                        delayedFetch();
                    });
            };

            const delayedFetch = () => {
                setTimeout(() => {
                    fetchMaterial();
                }, POLLING_DELAY);
            };

            fetchMaterial();
        });
    }
};

export default interactiveRedirectHelper;
